import isEmpty from 'lodash/isEmpty';
import { Fragment, useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';

import type {
  AccountApiError,
  AccountApiResponse,
} from '@/types/accountApiType';

import { Modal } from '@/components/Modal';
import { Typography } from '@/components/Typography';
import type { CountryCode } from '@/graphql/saleor/api';
import { useCatalogDownload } from '@/hooks/useCatalogDownload';
import { useUser } from '@/hooks/useUser';
import { getMetaValue } from '@/lib/address';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import * as M from '@/messages/catalogDownload';
import { userState } from '@/states/user';

import { Loader } from '../Loader';
import type { SelectOption } from '../Select';
import { CatalogDownloadForm } from './components/CatalogDownloadForm';
import type { CatalogDownloadSchema } from './components/CatalogDownloadForm/schema';

import * as S from './CatalogDownloadModal.styles';
export interface CatalogDownloadModalProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
}

export const CatalogDownloadModal = ({
  children,
  isOpen,
  onClose,
}: CatalogDownloadModalProps) => {
  const { catalogDownloadForGuest, catalogDownloadForUser } =
    useCatalogDownload();
  const [loading, setLoading] = useState<boolean>(false);
  const [isDownloaded, setIsDownloaded] = useState<boolean>(false);
  const [error, setError] = useState<AccountApiError | undefined>(undefined);
  const [userMetadataLanguage, setUserMetadataLanguage] = useState<
    string | null
  >(null);

  const {
    snapshot: { user },
  } = useUser();

  useEffect(() => {
    if (isOpen) {
      setIsDownloaded(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (user?.metadata) {
      const getUserMetadataLanguage = getMetaValue(
        ACCOUNT_METADATA.language,
        user.metadata,
      );
      setUserMetadataLanguage(getUserMetadataLanguage);
    }
  }, [user?.metadata]);

  const initData = async () => {
    setLoading(false);
  };

  const handleSubmit: SubmitHandler<CatalogDownloadSchema> = async ({
    catalogDownloadConsent,
    country,
    email,
    firstName,
    firstNameKanji,
    language,
    lastName,
    lastNameKanji,
    recaptchaResponse,
    salutation,
  }: CatalogDownloadSchema) => {
    const _country = country as SelectOption;
    const _language = language as SelectOption;
    const languageCode = _language?.value;
    const _salutation = salutation as SelectOption | null;

    try {
      let res: AccountApiResponse<null>;
      setLoading(true);
      if (userState.isAuthenticated) {
        res = await catalogDownloadForUser({
          catalogDownloadConsent,
          languageCode: userMetadataLanguage
            ? userMetadataLanguage
            : languageCode,
          recaptchaResponse,
          websiteCountryCode: _country?.value as CountryCode,
        });
      } else {
        res = await catalogDownloadForGuest({
          catalogDownloadConsent,
          email,
          firstName,
          lastName,
          firstNameKanji,
          lastNameKanji,
          languageCode,
          salutation: _salutation?.value ?? '',
          recaptchaResponse,
          websiteCountryCode: _country?.value as CountryCode,
        });
      }
      setLoading(false);
      if (res.code === 200) {
        setError(undefined);
        setIsDownloaded(true);
      } else {
        setError({ code: res.code, msg: res.msg || res.message });
      }
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userState.isAuthenticated) initData();
  }, [userState.isAuthenticated, isOpen]);

  const handleModalClose = () => {
    onClose();
  };

  return (
    <Modal
      data-test-id='CatalogDownloadModal'
      gutters={false}
      isOpen={isOpen}
      maxWidth='sm'
      onClose={handleModalClose}
    >
      <Modal.Header
        data-test-id='CatalogDownloadModalHeader'
        divider={true}
        onClose={handleModalClose}
      >
        <S.Title>{M.CatalogDownloadTitle}</S.Title>
      </Modal.Header>
      <Modal.Body data-test-id='CatalogDownloadModalBody'>
        {loading ? (
          <Loader data-test-id='CatalogDownloadModalLoader'></Loader>
        ) : (
          <>
            {!isDownloaded ? (
              <Fragment>
                {children}
                <CatalogDownloadForm
                  data-test-id='CatalogDownloadModalForm'
                  handleSubmit={handleSubmit}
                  isProcessing={loading}
                ></CatalogDownloadForm>
              </Fragment>
            ) : (
              <Typography
                data-test-id='CatalogDownloadModalSuccessMessage'
                margin={6}
                textAlign='center'
              >
                {userState.isAuthenticated
                  ? M.CatalogDownloadSuccessDescUser
                  : M.CatalogDownloadSuccessDescGuest}
              </Typography>
            )}
            {!isEmpty(error) && error.msg && (
              <Typography
                color='error'
                data-test-id='CatalogDownloadModalErrorMessage'
                scale='p3'
                textAlign='center'
              >
                {error.msg}
              </Typography>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

CatalogDownloadModal.displayName = 'CatalogDownloadModal';
